/**
 * Shared Gate 2 scoring helpers — one result row per catalog metric in scope.
 */
import { scoreExecutionMetric } from "../../services/gate2ExecutionClient";
import { isBlockedExtraCgMetric, isGate2ExecutableMetric } from "../../utils/executableMetrics";
import { METRICS_NO_FAKE_ANSWER, isScorableOutcome } from "./implementedRagMetrics.js";
import { TOOL_SPAN_METRIC_IDS } from "../../utils/metricConnectors";

function isScorableCase(c) {
  return (
    c.outcome === "retrieved" ||
    Boolean(String(c.answer || "").trim()) ||
    (c.chunks || []).some((ch) => ch.content) ||
    (c.traces || []).length > 0 ||
    Boolean(c.evidence?.actualOutput) ||
    (c.evidence?.tools || []).length > 0 ||
    Boolean(c.evidence?.toolsDeclared) ||
    c.evidence?.httpStatus != null
  );
}

function caseHasTools(c) {
  return (c.evidence?.tools || []).length > 0;
}

function isJobScorable(c, metricId) {
  if (metricId && isScorableOutcome(c.outcome, metricId)) return true;
  return isScorableCase(c);
}

export function toScoreRow(c, metricId) {
  const contexts = (c.chunks || []).map((chunk) => chunk.content).filter(Boolean);
  const answer = String(c.answer || c.evidence?.actualOutput || c.error?.message || "").trim();
  const toolsCalled = (c.evidence?.tools || []).map((t) => t.name).filter(Boolean);
  const statusCode = c.statusCode ?? c.evidence?.httpStatus ?? c.queried?.api?.statusCode ?? null;
  const retrievedDocIds = (c.chunks || [])
    .map((chunk) => chunk.documentId || chunk.document_id)
    .filter(Boolean);
  const row = {
    testCaseId: c.id,
    input: c.prompt,
    expected_output: c.expected,
    retrieval_context: contexts,
    retrieved_doc_ids: retrievedDocIds,
    retrieval_outcome: c.outcome,
    chunks: c.chunks || [],
    tools_called: toolsCalled,
    expected_tools: c.evidence?.expectedTools || c.expected_tools || [],
    expected_doc_ids: c.expected_doc_ids || c.expectedDocIds || [],
    status_code: statusCode,
    statusCode,
    latencyMs: c.latencyMs ?? null,
    extras: c.extras || {},
    ok: c.outcome === "retrieved",
    evidence: c.evidence || null,
  };
  if (metricId && METRICS_NO_FAKE_ANSWER.includes(metricId)) {
    if (answer) row.actual_output = answer;
  } else {
    row.actual_output = answer || contexts.join("\n\n");
  }
  return row;
}

export function jobsForSelectedItem(item, executionCases, metricsById) {
  if (item.catalogMetricId) {
    const catalogId = item.catalogMetricId || item.formulaFn;
    if (
      isBlockedExtraCgMetric(catalogId, item.name) ||
      !isGate2ExecutableMetric(catalogId, item.name)
    ) {
      return [];
    }
    const sourceCases = item.executableCases || item.cases;
    const itemCases = sourceCases
      .map((tc) => executionCases.find((c) => c.id === tc.id))
      .filter(Boolean);
    const scorable = itemCases.filter((c) => isJobScorable(c, catalogId));
    return [
      {
        key: String(item.id),
        metricId: catalogId,
        metricName: item.name,
        suiteName: item.name,
        suiteId: null,
        scorable,
        skipped: itemCases.length - scorable.length,
        collectCaseIds: sourceCases.map((tc) => tc.id),
      },
    ];
  }

  if (isBlockedExtraCgMetric("", item.name)) return [];
  const groups = new Map();
  for (const tc of item.cases) {
    const register = metricsById.get(String(tc.metricId));
    const catalogId = register?.metricId || register?.formulaFn;
    if (!catalogId) continue;
    if (
      isBlockedExtraCgMetric(catalogId, register?.metricName) ||
      !isGate2ExecutableMetric(catalogId, register?.metricName)
    ) {
      continue;
    }
    if (!groups.has(catalogId)) {
      groups.set(catalogId, {
        key: `${item.id}:${catalogId}`,
        metricId: catalogId,
        metricName: register?.metricName || catalogId,
        suiteName: item.name,
        suiteId: item.id,
        scorable: [],
        collectCaseIds: [],
        total: 0,
      });
    }
    const group = groups.get(catalogId);
    group.total += 1;
    group.collectCaseIds.push(tc.id);
    const exec = executionCases.find((c) => c.id === tc.id);
    if (exec && isJobScorable(exec, catalogId)) group.scorable.push(exec);
  }
  return Array.from(groups.values()).map((group) => ({
    key: group.key,
    metricId: group.metricId,
    metricName: group.metricName,
    suiteName: group.suiteName,
    suiteId: group.suiteId,
    scorable: group.scorable,
    skipped: group.total - group.scorable.length,
    collectCaseIds: group.collectCaseIds,
  }));
}

/** Flat list of metrics shown in the execution progress dialog. */
export function listProgressMetrics(selectedItems, metricsById) {
  const rows = [];
  for (const item of selectedItems) {
    const jobs = jobsForSelectedItem(item, [], metricsById);
    if (jobs.length) {
      for (const job of jobs) {
        rows.push({ key: job.key, name: job.metricName });
      }
    } else {
      rows.push({ key: String(item.id), name: item.name });
    }
  }
  return rows;
}

export function flattenExecutionJobs(selectedItems, executionCases, metricsById) {
  const jobs = [];
  for (const item of selectedItems) {
    jobs.push(...jobsForSelectedItem(item, executionCases, metricsById));
  }
  return jobs;
}

export function metricPassed(result) {
  if (!result || result.error || result.value == null) return false;
  if (result.status === "not_implementable" || result.status === "not_evaluated") return false;
  return Boolean(result.passed);
}

/**
 * Score all jobs for the current selection; returns API result rows (same shape as before).
 */
export async function scoreSelectedExecutionJobs({
  projectId,
  runId,
  selectedItems,
  executionCases,
  metricsById,
  isContentGenerator = false,
}) {
  const results = [];
  for (const item of selectedItems) {
    const jobs = jobsForSelectedItem(item, executionCases, metricsById);
    if (!jobs.length) {
      results.push({
        key: item.id,
        metricName: item.name,
        suiteName: item.name,
        value: null,
        perCase: [],
        error: `No test case in "${item.name}" is linked to a metric in the register.`,
      });
      continue;
    }
    for (const job of jobs) {
      const ownHasTools = job.scorable.some(caseHasTools);
      if (TOOL_SPAN_METRIC_IDS.has(job.metricId) && !ownHasTools) {
        const borrowed = executionCases.filter((c) => isScorableCase(c) && caseHasTools(c));
        if (borrowed.length) job.scorable = borrowed;
      }
      if (!job.scorable.length) {
        results.push({
          key: job.key,
          metricName: job.metricName,
          suiteName: job.suiteName,
          value: null,
          perCase: [],
          error: isContentGenerator
            ? `No test case in "${item.name}" produced an application answer, so there is nothing to score.`
            : `No test case in "${job.metricName}" collected any evidence, so there is nothing to score.`,
        });
        continue;
      }
      const payload = {
        ...(runId ? { run_id: runId } : {}),
        ...(job.suiteId ? { suite_id: job.suiteId } : {}),
        ...(job.metricId ? { metric_id: job.metricId } : {}),
        suite_name: job.metricName,
        ...(job.metricId === "rm_response_consistency_score" ? { consistency_repeats: 3 } : {}),
        cases: job.scorable.map((c) => toScoreRow(c, job.metricId)),
      };
      try {
        const res = await scoreExecutionMetric(projectId, payload);
        results.push({ key: job.key, skipped: job.skipped, ...(res?.data || {}) });
      } catch (err) {
        const data = err.response?.data;
        results.push({
          key: job.key,
          metricName: data?.details?.metricName || job.metricName,
          suiteName: job.suiteName,
          value: null,
          perCase: job.scorable.map((c) => ({ testCaseId: c.id, score: null, passed: null })),
          skipped: job.skipped,
          error: data?.error || err.message || "Scoring failed.",
        });
      }
    }
  }
  return results;
}

export async function scoreSingleJob({ projectId, runId, job, executionCases }) {
  const ownHasTools = job.scorable.some(caseHasTools);
  if (TOOL_SPAN_METRIC_IDS.has(job.metricId) && !ownHasTools) {
    const borrowed = executionCases.filter((c) => isScorableCase(c) && caseHasTools(c));
    if (borrowed.length) job.scorable = borrowed;
  }
  if (!job.scorable.length) {
    return {
      key: job.key,
      metricName: job.metricName,
      suiteName: job.suiteName,
      value: null,
      perCase: [],
      error: `No test case in "${job.metricName}" collected any evidence, so there is nothing to score.`,
    };
  }
  const payload = {
    ...(runId ? { run_id: runId } : {}),
    ...(job.suiteId ? { suite_id: job.suiteId } : {}),
    ...(job.metricId ? { metric_id: job.metricId } : {}),
    suite_name: job.metricName,
    ...(job.metricId === "rm_response_consistency_score" ? { consistency_repeats: 3 } : {}),
    cases: job.scorable.map((c) => toScoreRow(c, job.metricId)),
  };
  try {
    const res = await scoreExecutionMetric(projectId, payload);
    return { key: job.key, skipped: job.skipped, ...(res?.data || {}) };
  } catch (err) {
    const data = err.response?.data;
    return {
      key: job.key,
      metricName: data?.details?.metricName || job.metricName,
      suiteName: job.suiteName,
      value: null,
      perCase: job.scorable.map((c) => ({ testCaseId: c.id, score: null, passed: null })),
      skipped: job.skipped,
      error: data?.error || err.message || "Scoring failed.",
    };
  }
}
